import type { Customer } from '@order/graphql';
import { useCart } from '@order/hooks';

import { useGetCustomerCreditQuery } from '../GraphQL/Credit.generated';

export const useCustomerCredit = (props?: UseCustomerCreditProps) => {
  const [response, refetchCredits] = useGetCustomerCreditQuery({
    requestPolicy: 'network-only',
  });
  const data = getCreditData(response?.data?.currentCustomer);
  const { availableCredit, credits, useCredit } = data;

  // The credit is always in relation to the total cost of the purchase.
  const { total } = useCart();
  const checkoutCredit = getCheckoutCredit(
    total,
    response.data?.currentCustomer?.availableCredit,
    props?.deliveryTip,
  );

  return {
    availableCredit,
    checkoutCredit,
    credits,
    useCredit,
    isLoadingCredits: response.fetching || response.stale,
    refetchCredits,
  };
};

const getCheckoutCredit = (
  total?: number,
  availableCredit?: number,
  deliveryTip?: number,
) => {
  const totalWithTip = (total ?? 0) + (deliveryTip ?? 0);

  return Math.min(totalWithTip, availableCredit ?? 0);
};

const getCreditData = (response?: Partial<CreditResponse> | null) => {
  return {
    ...defaultCreditResponse,
    ...response,
  };
};

const defaultCreditResponse: CreditResponse = {
  availableCredit: 0,
  useCredit: false,
  credits: [],
};

type UseCustomerCreditProps = {
  readonly deliveryTip: number;
};

type CreditResponse = {
  readonly availableCredit: number;
  readonly useCredit: boolean;
  readonly credits: Customer['credits'];
};
